// REFACTORED: 2026-02-02
// FROM src/components/layout/DashboardLayout.tsx

import { parse, format, isValid } from 'date-fns';
import type { FilterRequest } from '../types/api';

// ============================================================================
// DATE & DURATION UTILITIES
// ============================================================================

/**
 * Rolling last 12 months (inclusive of current month), e.g. for My Team filters.
 */
export const getDefaultRolling12MonthRange = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  return {
    start_year: currentYear,
    start_month: 1,
    end_year: currentYear,
    end_month: currentMonth,
  };
};

/** Full `FilterRequest` defaults for My Team (rolling 12 months + empty lists). */
export const getDefaultMyTeamFilterRequest = (): FilterRequest => ({
  ...getDefaultRolling12MonthRange(),
  employee_status: 'ACTIVE',
  business_units: [],
  sub_bus: [],
  employee_names: [],
  managers: [],
  project_codes: [],
  project_status: [],
  designations: [],
});

/**
 * Get default duration for dashboard filters (last 12 months)
 * Returns an object with start_year, start_month, end_year, end_month
 */
export const getDefaultDuration = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-based index

  let startYear = currentYear;
  let startMonth = 1;

  return {
    start_year: startYear,
    start_month: startMonth,
    end_year: currentYear,
    end_month: currentMonth,
  };
};

// REFACTORED: 2026-02-02
// FROM src/components/project/ProjectAnalytics.tsx

/**
 * Get default month range (last 12 months)
 * Used for Monthly New Projects chart
 */
export const getDefaultMonths = (): { startMonth: { year: number; month: number }; endMonth: { year: number; month: number } } => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-based month
  
  // Calculate end month (current month)
  const endMonth = { year: currentYear, month: currentMonth };
  
  // Calculate start month (12 months ago)
  let startYear = currentYear;
  let startMonthNum = currentMonth - 11; // 12 months back
  
  if (startMonthNum <= 0) {
    startMonthNum += 12;
    startYear -= 1;
  }
  
  const startMonth = { year: startYear, month: startMonthNum };
  
  return { startMonth, endMonth };
};

/**
 * Check if duration is outside the filtered opportunity closed year/quarter range
 * Used to show warning in Monthly New Projects chart
 */
export const isDurationOutOfBounds = (
  startMonth: { year: number; month: number } | null,
  endMonth: { year: number; month: number } | null,
  filters?: any
): boolean => {
  if (!filters || !startMonth || !endMonth) return false;
  
  const years = filters.opportunity_closed_years;
  const quarters = filters.opportunity_closed_quarters;
  
  // If no filters are applied, duration is always in bounds
  if (!years || years.length === 0 || years.length >= 6) {
    if (!quarters || quarters.length === 0 || quarters.length >= 4) {
      return false;
    }
  }
  
  // Get all valid months based on year and quarter filters
  const validMonths: Set<string> = new Set();
  
  // Process years
  const yearsToCheck = years && years.length > 0 && years.length < 6 ? years : null;
  const quartersToCheck = quarters && quarters.length > 0 && quarters.length < 4 ? quarters : null;
  
  if (yearsToCheck) {
    yearsToCheck.forEach((year: number) => {
      if (quartersToCheck) {
        // If quarters are specified, only include those quarters
        quartersToCheck.forEach((quarter: string) => {
          let startMonthNum = 1;
          let endMonthNum = 12;
          
          if (quarter === 'Q1') {
            startMonthNum = 1;
            endMonthNum = 3;
          } else if (quarter === 'Q2') {
            startMonthNum = 4;
            endMonthNum = 6;
          } else if (quarter === 'Q3') {
            startMonthNum = 7;
            endMonthNum = 9;
          } else if (quarter === 'Q4') {
            startMonthNum = 10;
            endMonthNum = 12;
          }
          
          for (let month = startMonthNum; month <= endMonthNum; month++) {
            validMonths.add(`${year}-${month}`);
          }
        });
      } else {
        // If no quarters specified, include all months for the year
        for (let month = 1; month <= 12; month++) {
          validMonths.add(`${year}-${month}`);
        }
      }
    });
  } else if (quartersToCheck) {
    // If only quarters are specified without years, we can't determine the range
    // In this case, we'll assume it's not out of bounds
    return false;
  }
  
  // Check if startMonth or endMonth is outside valid range
  const startKey = `${startMonth.year}-${startMonth.month}`;
  const endKey = `${endMonth.year}-${endMonth.month}`;
  
  // Check if any month in the selected range is outside valid months
  const isStartOutOfBounds = !validMonths.has(startKey);
  const isEndOutOfBounds = !validMonths.has(endKey);
  
  // Also check all months in between
  let hasOutOfBoundsMonth = isStartOutOfBounds || isEndOutOfBounds;
  
  if (!hasOutOfBoundsMonth) {
    // Check all months in the range
    let currentYear = startMonth.year;
    let currentMonth = startMonth.month;
    const endYear = endMonth.year;
    const endMonthNum = endMonth.month;
    
    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonthNum)) {
      const monthKey = `${currentYear}-${currentMonth}`;
      if (!validMonths.has(monthKey)) {
        hasOutOfBoundsMonth = true;
        break;
      }
      
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
  }
  
  return hasOutOfBoundsMonth;
};

// ============================================================================
// DATE PARSING & FORMATTING (DRI Insights and general use)
// ============================================================================

/** Date formats tried when parsing strings (e.g. from API). */
export const DATE_PARSE_FORMATS = [
  'yyyy-MM-dd',
  'MM/dd/yyyy',
  'dd/MM/yyyy',
  'yyyy/MM/dd',
  'MM-dd-yyyy',
  'dd-MM-yyyy',
  'dd-Mon-yyyy',
] as const;

/**
 * Parse a date string into a Date using multiple formats.
 * Falls back to native Date parsing if none match.
 */
export const parseDateString = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  for (const fmt of DATE_PARSE_FORMATS) {
    try {
      const parsed = parse(dateStr, fmt, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    } catch {
      // Continue to next format
    }
  }

  const nativeDate = new Date(dateStr);
  return isValid(nativeDate) ? nativeDate : null;
};

/**
 * Format a date string as DD-Mon-YYYY (e.g. 03-Feb-2025).
 * Returns '-' for null/empty or invalid dates.
 */
export const formatDateEnhanced = (dateString: string | null): string => {
  if (!dateString) return '-';
  try {
    const date = parseDateString(dateString);
    if (!date || !isValid(date)) return '-';
    return format(date, 'dd-MMM-yyyy');
  } catch {
    return '-';
  }
};

